/**
 * User Department Hooks - React Query hooks for user department operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUserDepartments,
  addUserDepartment,
  updateUserDepartment,
  removeUserDepartment,
  checkAffectedOKRs,
  AddDepartmentMembershipRequest,
  UpdateDepartmentMembershipRequest,
  AffectedOKRsResult,
} from '../services/userDepartments';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Re-export types for convenience
export type { AffectedOKRsResult };

// Re-export checkAffectedOKRs for direct use in components
export { checkAffectedOKRs };

// Query keys
export const userDepartmentKeys = {
  all: ['userDepartments'] as const,
  user: (userId: string) => [...userDepartmentKeys.all, userId] as const,
};

/**
 * Get user's department memberships
 */
export const useUserDepartments = (userId: string) => {
  return useQuery({
    queryKey: userDepartmentKeys.user(userId),
    queryFn: () => getUserDepartments(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Add user to a department
 */
export const useAddUserDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AddDepartmentMembershipRequest }) =>
      addUserDepartment(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userDepartmentKeys.user(variables.userId) });
      toast.success('User added to department successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to add user to department'));
    },
  });
};

/**
 * Update user's department membership (set primary or update role)
 */
export const useUpdateUserDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      departmentId,
      data,
    }: {
      userId: string;
      departmentId: string;
      data: UpdateDepartmentMembershipRequest;
    }) => updateUserDepartment(userId, departmentId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userDepartmentKeys.user(variables.userId) });

      // Show appropriate success message
      if (variables.data.isPrimary) {
        toast.success('Primary department updated successfully');
      } else if (variables.data.role !== undefined) {
        toast.success('Department role updated successfully');
      } else {
        toast.success('Department membership updated successfully');
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update department membership'));
    },
  });
};

/**
 * Remove user from a department
 */
export const useRemoveUserDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, departmentId, force = false }: { userId: string; departmentId: string; force?: boolean }) =>
      removeUserDepartment(userId, departmentId, force),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: userDepartmentKeys.user(variables.userId) });
      // Also invalidate OKRs cache in case OKRs were reassigned
      if (result.reassignedOKRs > 0) {
        queryClient.invalidateQueries({ queryKey: ['okrs'] });
        toast.success(`Department removed. ${result.reassignedOKRs} OKR(s) reassigned to primary department.`);
      } else {
        toast.success('User removed from department successfully');
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to remove user from department'));
    },
  });
};
